import { Component, effect, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ApiModuleService,
  BuilderDashboardComponent,
  BuilderService,
  ClipboardService,
  HistoryService,
  UniqueIdService,
} from '@sportello-balneari/forms-engine';
import {
  exitConfirmationDialog,
  publishConfirmationDialog,
} from '@sportello-balneari/shared-components';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-new-module',
  templateUrl: './new-module.component.html',
  imports: [
    BuilderDashboardComponent,
    ButtonModule,
    MatIconModule,
    ConfirmDialogModule,
    ToastModule,
  ],
  providers: [
    BuilderService,
    HistoryService,
    UniqueIdService,
    ConfirmationService,
    ClipboardService,
    MessageService,
  ],
})
export class NewModuleComponent {
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  protected builderService = inject(BuilderService);
  protected historyService = inject(HistoryService);
  protected apiModuleService = inject(ApiModuleService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  tenderId = input.required<string>();

  constructor() {
    effect(() => {
      const tenderId = this.tenderId();
      if (!tenderId) return;

      this.apiModuleService.getModuleStructure(tenderId).subscribe((module) => {
        this.builderService.tenderFormId.set(module.id || undefined);
        this.builderService.tenderFormInfo.set({
          tenderId: tenderId,
          id: module.id || '',
          revision: module.revision,
          revisions: module.revisions,
          title: module.title || '',
        });
        if (module.form) {
          this.builderService.moduleStructure.set(module.form);
          this.historyService.setupStartingComponent(module.form);
        }
      });
    });
  }

  saveModule() {
    const moduleStructure = this.builderService.moduleStructure();

    const tenderFormId = this.builderService.tenderFormId();
    if (!tenderFormId) {
      this.apiModuleService
        .saveModule(this.tenderId(), moduleStructure)
        .subscribe({
          next: (val) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Successo',
              detail: 'Modulo salvato con successo',
            });
            this.builderService.tenderFormId.set(val.id);
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Errore durante il salvataggio del modulo',
            });
          },
        });
    } else {
      const tenderId = this.tenderId();
      this.apiModuleService.editModule(tenderId, moduleStructure).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: 'Modulo modificato con successo',
          });
          this.builderService.tenderFormId.set(tenderFormId);
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore durante la modifica del modulo',
          });
        },
      });
    }
  }

  publishModule() {
    publishConfirmationDialog(this.confirmationService, {
      position: 'center',
      message: 'Questa azione non può essere annullata.',
      accept: () => {
        const moduleStructure = this.builderService.moduleStructure();

        const tenderFormId = this.builderService.tenderFormId();
        if (!tenderFormId) return;

        const tenderId = this.tenderId();

        this.apiModuleService
          .editModule(tenderId, moduleStructure, 'Published')
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Successo',
                detail: 'Modulo pubblicato con successo',
              });
              this.builderService.tenderFormId.set(tenderFormId);
            },
            error: (err) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Errore',
                detail: 'Errore durante la pubblicazione del modulo',
              });
            },
          });
      },
    });
  }

  goBack() {
    const tenderId = this.tenderId();
    if (!tenderId) return;

    exitConfirmationDialog(this.confirmationService, {
      position: 'center',
      message: 'Tutte le modifiche non salvate andranno perse.',
      accept: () => {
        this.router.navigate([`../tenders/edit/${tenderId}`], {
          relativeTo: this.activatedRoute,
        });
      },
    });
  }
}
